import { useEffect } from "react";

declare global {
  interface Window {
    __AGES_ROUTER_MOUNT_COUNT__?: number;
    __AGES_ROUTER_MOUNT_LOCATIONS__?: string[];
  }
}

/**
 * Development-only detector for duplicate <BrowserRouter> mounts.
 *
 * Mount this as a child of every BrowserRouter in the app. On mount it
 * increments a window-level counter and logs a loud warning if more than
 * one router instance is alive at the same time — which is the usual
 * cause of "Cannot destructure property 'basename' of useContext(...) as
 * it is null" errors.
 *
 * Pass a `source` prop describing where the router lives (e.g.
 * "src/App.tsx") so the warning can point you straight at the offender.
 */
export function DuplicateRouterDetector({ source }: { source: string }) {
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    window.__AGES_ROUTER_MOUNT_COUNT__ =
      (window.__AGES_ROUTER_MOUNT_COUNT__ ?? 0) + 1;
    window.__AGES_ROUTER_MOUNT_LOCATIONS__ = [
      ...(window.__AGES_ROUTER_MOUNT_LOCATIONS__ ?? []),
      source,
    ];

    if ((window.__AGES_ROUTER_MOUNT_COUNT__ ?? 0) > 1) {
      console.warn(
        `[AGES] Duplicate <BrowserRouter> detected (${window.__AGES_ROUTER_MOUNT_COUNT__} instances).\n` +
          `Mounted from:\n  - ${window.__AGES_ROUTER_MOUNT_LOCATIONS__?.join("\n  - ")}\n` +
          `Only one BrowserRouter should exist in the tree. Check ${source} ` +
          `and any nested layout / page files for an extra <BrowserRouter>. ` +
          `Duplicate routers cause the "Cannot destructure property 'basename'" runtime error.`,
      );
    }

    return () => {
      if (!import.meta.env.DEV) return;
      window.__AGES_ROUTER_MOUNT_COUNT__ = Math.max(
        0,
        (window.__AGES_ROUTER_MOUNT_COUNT__ ?? 1) - 1,
      );
      window.__AGES_ROUTER_MOUNT_LOCATIONS__ =
        window.__AGES_ROUTER_MOUNT_LOCATIONS__?.filter(
          (s, i, arr) => !(s === source && i === arr.lastIndexOf(source)),
        ) ?? [];
    };
  }, [source]);

  return null;
}
